import { useEffect, useState, useCallback } from "react";
import { AdminApi, type AuditEntry, type PaginatedResponse } from "@/api/adminApi";
import { RestClient } from "@/api/restClient";

const apiUrl = import.meta.env.VITE_API_URL || "";
const client = new RestClient({ apiUrl });
const adminApi = new AdminApi(client);

const ACTION_COLORS: Record<string, string> = {
  "user.suspend": "#dc2626",
  "user.unsuspend": "#16a34a",
  "org.suspend": "#dc2626",
  "org.unsuspend": "#16a34a",
  "org.grant_sponsor": "#7c3aed",
  "org.revoke_sponsor": "#ea580c",
  "platform_admin.add": "#2563eb",
  "platform_admin.remove": "#ea580c",
  "user.impersonate": "#ca8a04",
};

export function AdminAudit() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAuditLog({
        page,
        limit: 50,
        action: actionFilter || undefined,
      });
      setEntries(res.items);
      setTotal(res.total);
    } catch {
      // error
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Audit Log</h1>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          style={{ padding: "6px 12px", border: "1px solid var(--border-color, #e0e0e0)", borderRadius: 6, fontSize: 13 }}
        >
          <option value="">All actions</option>
          <option value="user.suspend">User Suspend</option>
          <option value="user.unsuspend">User Unsuspend</option>
          <option value="user.impersonate">User Impersonate</option>
          <option value="user.add_to_org">User Add to Org</option>
          <option value="user.remove_from_org">User Remove from Org</option>
          <option value="user.change_role">User Change Role</option>
          <option value="org.suspend">Org Suspend</option>
          <option value="org.unsuspend">Org Unsuspend</option>
          <option value="org.grant_sponsor">Org Grant Sponsor</option>
          <option value="org.revoke_sponsor">Org Revoke Sponsor</option>
          <option value="org.grant_sponsored_seats">Org Grant Seats</option>
          <option value="platform_admin.add">Admin Add</option>
          <option value="platform_admin.remove">Admin Remove</option>
        </select>
        <span style={{ fontSize: 13, color: "var(--text-secondary, #666)", alignSelf: "center" }}>
          {total} entries
        </span>
      </div>

      {/* Table */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid var(--border-color, #e0e0e0)" }}>
            <th style={{ textAlign: "left", padding: "8px 4px" }}>Time</th>
            <th style={{ textAlign: "left", padding: "8px 4px" }}>Admin</th>
            <th style={{ textAlign: "left", padding: "8px 4px" }}>Action</th>
            <th style={{ textAlign: "left", padding: "8px 4px" }}>Target</th>
            <th style={{ textAlign: "left", padding: "8px 4px" }}>Details</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5} style={{ padding: 16, textAlign: "center" }}>Loading...</td></tr>
          ) : entries.length === 0 ? (
            <tr><td colSpan={5} style={{ padding: 16, textAlign: "center" }}>No audit entries</td></tr>
          ) : (
            entries.map((e) => (
              <tr key={e.id} style={{ borderBottom: "1px solid var(--border-color, #f0f0f0)" }}>
                <td style={{ padding: "8px 4px", whiteSpace: "nowrap", color: "var(--text-secondary, #666)" }}>
                  {new Date(e.created_at).toLocaleString()}
                </td>
                <td style={{ padding: "8px 4px", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {e.admin_id.slice(0, 12)}…
                </td>
                <td style={{ padding: "8px 4px" }}>
                  <span style={{
                    fontSize: 11,
                    padding: "2px 6px",
                    borderRadius: 4,
                    background: `${ACTION_COLORS[e.action] || "#6b7280"}15`,
                    color: ACTION_COLORS[e.action] || "#6b7280",
                    fontWeight: 500,
                  }}>
                    {e.action}
                  </span>
                </td>
                <td style={{ padding: "8px 4px" }}>
                  <span style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>{e.target_type}:</span>{" "}
                  {e.target_id.slice(0, 12)}…
                </td>
                <td style={{ padding: "8px 4px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", fontSize: 11, color: "var(--text-secondary, #666)" }}>
                  {Object.keys(e.details).length > 0 ? JSON.stringify(e.details) : "—"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16, fontSize: 13 }}>
        <button disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</button>
        <span>Page {page}</span>
        <button disabled={entries.length < 50} onClick={() => setPage(page + 1)}>Next →</button>
      </div>
    </div>
  );
}
